/**
 * Shared DataGrid SX tokens.
 *
 * RATDataGrid always merges `ratDataGridSx` first; caller `sx` is merged after
 * so implementers can override any default. Features that fill a flex panel can
 * also pass `ratDataGridFillSx` (or spread it into their own `sx` object).
 */
import { GRID_DETAIL_PANEL_TOGGLE_FIELD } from "@mui/x-data-grid-pro";
import colors from "theme/colors";

/** RulesTable / classic RAT table header strip (`#F4F8FC`). */
const HEADER_FOOTER_BG = colors.tables.columnsAndRowsTitles;

/**
 * Default presentation for all RAT grids:
 * - Header + footer tinted with `columnsAndRowsTitles`
 * - Pinned headers keep MUI default (paper) via `--DataGrid-pinnedBackground`
 * - Pinned body cells stay white
 * - Cells vertically centered (auto-height / multi-line content)
 * - Master-detail expand/collapse toggle centered in its column
 */
export const ratDataGridSx = {
  // Drop MUI's default 1px root border — product chrome supplies page boundaries.
  border: "none",

  // MUI v7 paints the header/footer *row* backgrounds via these CSS variables
  // (not only via `.columnHeaders`). Unpinned header cells sit on
  // `--DataGrid-containerBackground`. Pinned headers use
  // `--DataGrid-pinnedBackground` (left at MUI default / paper — not overridden).
  "--DataGrid-containerBackground": HEADER_FOOTER_BG,

  // Column-header strip container (belt-and-suspenders with the CSS var above).
  "& .MuiDataGrid-columnHeaders": {
    backgroundColor: HEADER_FOOTER_BG,
    // Let wrapped header titles grow past the default fixed header height.
    maxHeight: "none !important",
  },

  // Each column header cell: allow natural height while keeping a usable minimum.
  "& .MuiDataGrid-columnHeader": {
    height: "unset !important",
    minHeight: 56,
  },

  // Header label text: bold, wrap onto multiple lines instead of ellipsis.
  "& .MuiDataGrid-columnHeaderTitle": {
    fontWeight: "bold",
    whiteSpace: "normal",
    lineHeight: "normal",
    overflow: "visible",
  },

  // Wrapper around the title — also allow wrapping (MUI defaults to nowrap).
  "& .MuiDataGrid-columnHeaderTitleContainer": {
    whiteSpace: "normal",
  },

  // Innermost title slot — don't clip multi-line labels.
  "& .MuiDataGrid-columnHeaderTitleContainerContent": {
    overflow: "visible",
  },

  // "Rows Displayed" / pagination bar. This slot does NOT read
  // `--DataGrid-containerBackground`, so the tint must be set explicitly.
  "& .MuiDataGrid-footerContainer": {
    // Put the row-count on the left (MUI defaults to space-between / right bias).
    justifyContent: "flex-start",
    backgroundColor: HEADER_FOOTER_BG,
  },

  // Footer row-count label: sit flush left and grow so it doesn't crowd controls.
  "& .MuiDataGrid-rowCount": {
    marginLeft: 0,
    marginRight: "auto",
  },

  // Pinned *body* cells: keep white so feature chrome (e.g. Table Summary expand
  // card tint) can show through. Use the `background` shorthand to beat MUI's
  // `background: var(--DataGrid-pinnedBackground)` on pinned cells.
  "& .MuiDataGrid-cell--pinnedLeft, & .MuiDataGrid-cell--pinnedRight": {
    background: colors.white,
  },

  // Body cells: vertical padding + vertical centering.
  // MUI v7 cells are not `display: flex` by default, so auto-height / wrap rows
  // would otherwise top-align content (chips, multi-line values, etc.).
  "& .MuiDataGrid-cell": {
    py: 1,
    display: "flex",
    alignItems: "center",
  },
  // Editing cells must not use the view-mode flex/padding chrome — it breaks
  // row-edit inputs / singleSelect and can leave the grid stuck in edit mode
  // (reorder handles + parent "editing" flags stay disabled).
  "& .MuiDataGrid-cell--editing": {
    py: 0,
    display: "flex",
    alignItems: "stretch",
  },

  // Master-detail expand/collapse (+) column: center the icon in the cell and
  // header. Only present when a grid supplies `getDetailPanelContent`; harmless
  // no-op otherwise (no matching data-field nodes).
  [`& .MuiDataGrid-cell[data-field="${GRID_DETAIL_PANEL_TOGGLE_FIELD}"]`]: {
    justifyContent: "center",
  },
  [`& .MuiDataGrid-columnHeader[data-field="${GRID_DETAIL_PANEL_TOGGLE_FIELD}"]`]:
    {
      justifyContent: "center",
    },
} as const;

/**
 * Layout helper for grids that should fill a flex parent (e.g. product-detail
 * panels). Footer stays pinned at the bottom of the grid; only the row area
 * scrolls. Ancestors need `display: flex; flex-direction: column; min-height: 0`.
 */
export const ratDataGridFillSx = {
  // Stretch to fill the parent flex column.
  display: "flex",
  flexDirection: "column",
  flex: 1,
  // Allow this flex child to shrink below content size (critical for nested scroll).
  minHeight: 0,
  height: "100%",
  width: "100%",
  // Horizontal inset from the panel edges; no top/bottom padding so header/footer
  // align with the content band.
  padding: "0 16px 0px",
  // Clip any overflow to this box — scrolling happens inside the virtual scroller.
  overflow: "hidden",

  // Main grid body (headers + rows): take remaining height above the footer.
  "& .MuiDataGrid-main": {
    flex: 1,
    minHeight: 0,
    overflow: "hidden",
  },

  // Virtualized row viewport: the only vertical scroll container.
  "& .MuiDataGrid-virtualScroller": {
    flex: 1,
    minHeight: 0,
  },
} as const;
